import { useState } from 'react';
import { Box, ButtonBase, Chip, Collapse, Typography, alpha, useTheme } from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOffRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { ItensDaVenda, NotaFiscalVenda } from './ResumoVendaListagem';
import dayjs from 'dayjs';
import { IListagemMovimento } from '../../../shared/services/api/estoque/venda/venda';

// Uma venda no celular (mesmo padrão do LinhaLancamentoMobile do Finanças
// Pessoais): a tabela tem colunas demais pra ~390px — aqui fica avatar com
// as iniciais | cliente (data, nº e operação embaixo) | total. Tocar abre a
// venda. Venda não enviada ao servidor ganha a mesma marcação da tabela
// (fundo de alerta + "Não enviada").
interface ILinhaVendaMobileProps {
  venda: IListagemMovimento;
  onAbrir: () => void;
}

// Cor/rótulo da tag de cancelamento/devolução do PDV — mesmo critério de
// cor usado no próprio Resumo de Vendas do PDV (DialogResumoVendasPDV.tsx):
// cancelada em vermelho, devolvida em laranja.
const BADGE_STATUS: Record<'cancelada' | 'devolvida', { label: string; cor: string }> = {
  cancelada: { label: 'Cancelada', cor: '#D32F2F' },
  devolvida: { label: 'Devolvida', cor: '#F57C00' },
};

export const LinhaVendaMobile: React.FC<ILinhaVendaMobileProps> = ({ venda, onAbrir }) => {
  const theme = useTheme();
  const corAlerta = venda.statusOffline === 'erro' ? theme.palette.error.main : theme.palette.warning.main;
  const badge = venda.status ? BADGE_STATUS[venda.status] : null;
  const nome = venda.nome || 'Cliente não informado';
  const iniciais = nome.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
  const detalhes = [
    dayjs(venda.emissao).format('DD/MM/YY'),
    venda.offline ? null : venda.numero ? `Nº ${venda.numero}` : null,
    venda.descricao || null,
  ].filter(Boolean).join(' · ');

  // Itens abertos no próprio cartão (sem precisar abrir a venda).
  const [itensAbertos, setItensAbertos] = useState(false);

  return (
    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
    <ButtonBase
      onClick={onAbrir}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        justifyContent: 'flex-start',
        textAlign: 'left',
        px: 2,
        py: 1.25,
        ...(venda.offline && {
          bgcolor: alpha(corAlerta, 0.08),
          boxShadow: `inset 3px 0 0 ${corAlerta}`,
        }),
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '12px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.8rem',
          fontWeight: 700,
          bgcolor: venda.offline ? alpha(corAlerta, 0.15) : alpha(theme.palette.primary.main, 0.1),
          color: venda.offline ? corAlerta : 'primary.main',
        }}
      >
        {venda.offline ? <CloudOffIcon sx={{ fontSize: 20 }} /> : iniciais || '?'}
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={700} noWrap>{nome}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div">{detalhes}</Typography>
        {venda.offline && (
          <Typography variant="caption" fontWeight={700} noWrap component="div" sx={{ color: venda.statusOffline === 'erro' ? 'error.main' : 'warning.dark' }}>
            {venda.statusOffline === 'erro' ? `Não enviada — erro: ${venda.erroOffline || 'recusada'}` : 'Não enviada ao servidor'}
          </Typography>
        )}
      </Box>
      <Box textAlign="right" flexShrink={0}>
        <Typography
          variant="body2"
          fontWeight={800}
          color={badge ? 'text.disabled' : 'success.main'}
          sx={{ whiteSpace: 'nowrap', textDecoration: badge ? 'line-through' : 'none' }}
        >
          {Number(venda.total).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
        </Typography>
        {badge && (
          <Chip
            label={badge.label}
            size="small"
            sx={{
              height: '18px', fontSize: '0.625rem', fontWeight: 700, mt: 0.25,
              color: badge.cor, bgcolor: alpha(badge.cor, 0.12),
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
      </Box>
      <ChevronRightRounded sx={{ color: 'text.disabled', ml: -0.5 }} />
    </ButtonBase>
    {/* Nota fiscal + "Ver itens": fora do botão do cartão (tocar no cartão
        continua abrindo a venda). Venda offline ainda não tem itens no servidor. */}
    {!venda.offline && (
      <Box sx={{ px: 2, pb: itensAbertos ? 1.25 : 0.75, mt: -0.5 }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ pl: '50px' }}>
          <Box flex={1} minWidth={0}>
            <NotaFiscalVenda tipo={venda.nftipo} numero={venda.nfnumero} status={venda.nfstatus} compacto />
          </Box>
          <ButtonBase
            onClick={() => setItensAbertos((v) => !v)}
            sx={{ px: 1, py: 0.25, borderRadius: 1, color: 'primary.main', fontSize: '0.72rem', fontWeight: 700, gap: 0.25 }}
          >
            {itensAbertos ? 'Esconder itens' : 'Ver itens'}
            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, transition: 'transform 150ms ease', transform: itensAbertos ? 'rotate(180deg)' : 'none' }} />
          </ButtonBase>
        </Box>
        <Collapse in={itensAbertos} unmountOnExit>
          <Box sx={{ mt: 1, p: 1.25, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.04), border: `1px solid ${theme.palette.divider}` }}>
            <ItensDaVenda idmov={venda.idmov} compacto />
          </Box>
        </Collapse>
      </Box>
    )}
    </Box>
  );
};
